import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { NOTCH_POSITIONS } from '../models/dice-faces.const';

/**
 * Dice Geometry Utilities
 *
 * Implements procedural dice geometry generation based on Codrops tutorial.
 * Creates rounded box with carved notches (no textures).
 *
 * **Algorithm Overview:**
 * 1. Start with BoxGeometry with high segment count
 * 2. Round edges using sphere subdivision (subCube + addition vectors)
 * 3. Merge duplicate vertices and recompute normals
 * 4. Apply smooth notches using cosine wave function
 * 5. Create inner black planes for notch contrast
 *
 * **Standard Die Configuration:**
 * - Opposite faces sum to 7 (1↔6, 2↔5, 3↔4)
 * - Face 1: Y+ (top) - 1 dot centered
 * - Face 2: X+ (right) - 2 dots diagonal
 * - Face 3: Z+ (front) - 3 dots diagonal
 * - Face 4: Z- (back) - 4 dots in corners
 * - Face 5: X- (left) - 5 dots (4 corners + center)
 * - Face 6: Y- (bottom) - 6 dots (2 rows of 3)
 *
 * @see https://tympanus.net/codrops/2023/01/25/crafting-a-dice-roller-with-three-js-and-cannon-es/
 */

export interface DiceGeometryParams {
  size: number; // Die size (e.g., 0.6)
  segments: number; // Segment count for BoxGeometry (e.g., 40)
  edgeRadius: number; // Radius for rounded edges (e.g., 0.08)
  notchRadius: number; // Radius of each notch dot (e.g., 0.15)
  notchDepth: number; // Depth of notch carving (e.g., 0.17)
}

/**
 * Creates complete dice mesh with outer rounded box and inner black planes.
 *
 * @param params Geometry parameters
 * @returns THREE.Group containing outer mesh (white) and inner mesh (black)
 */
export function createDiceMesh(params: DiceGeometryParams): THREE.Group {
  const outerGeometry = createRoundedDiceGeometry(params);
  const innerGeometry = createInnerGeometry(params);

  const outerMaterial = new THREE.MeshStandardMaterial({
    color: 0xeeeeee,
    roughness: 0.7,
    metalness: 0.1,
  });

  const innerMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 0,
    metalness: 1,
    side: THREE.DoubleSide,
  });

  const diceMesh = new THREE.Group();
  const innerMesh = new THREE.Mesh(innerGeometry, innerMaterial);
  const outerMesh = new THREE.Mesh(outerGeometry, outerMaterial);

  // Enable shadows on child meshes
  innerMesh.castShadow = true;
  innerMesh.receiveShadow = true;
  outerMesh.castShadow = true;
  outerMesh.receiveShadow = true;

  diceMesh.add(innerMesh, outerMesh);
  diceMesh.name = 'dice-mesh'; // For identification in scene
  diceMesh.castShadow = true;
  diceMesh.receiveShadow = true;

  console.log('[DiceGeometry] Created dice mesh:', diceMesh);
  console.log('[DiceGeometry] Outer mesh vertices:', outerGeometry.attributes['position'].count);
  console.log('[DiceGeometry] Inner mesh vertices:', innerGeometry.attributes['position'].count);

  return diceMesh;
}

/**
 * Creates rounded box geometry with carved notches.
 *
 * **Algorithm (from Codrops):**
 * 1. Create BoxGeometry with high segment count
 * 2. Loop through all vertices
 * 3. For vertices near edges/corners, apply rounding algorithm
 * 4. Apply notch carving to face surfaces
 * 5. Merge duplicate vertices (removes seams)
 * 6. Recompute normals for smooth shading
 *
 * @param params Geometry parameters
 * @returns THREE.BufferGeometry
 */
export function createRoundedDiceGeometry(
  params: DiceGeometryParams
): THREE.BufferGeometry {
  // Start with highly segmented box
  let boxGeometry = new THREE.BoxGeometry(
    params.size,
    params.size,
    params.size,
    params.segments,
    params.segments,
    params.segments
  );

  const positionAttr = boxGeometry.attributes['position'];
  const subCubeHalfSize = (params.size / 2) - params.edgeRadius;

  // Round edges and corners
  for (let i = 0; i < positionAttr.count; i++) {
    let position = new THREE.Vector3().fromBufferAttribute(positionAttr, i);

    // Calculate subCube vector (points to smaller inner cube)
    const subCube = new THREE.Vector3(
      Math.sign(position.x),
      Math.sign(position.y),
      Math.sign(position.z)
    ).multiplyScalar(subCubeHalfSize);

    // Calculate addition vector (difference from subCube)
    const addition = new THREE.Vector3().subVectors(position, subCube);

    // Check if vertex is near corner or edge
    if (
      Math.abs(position.x) > subCubeHalfSize &&
      Math.abs(position.y) > subCubeHalfSize &&
      Math.abs(position.z) > subCubeHalfSize
    ) {
      // Near corner - project onto sphere
      addition.normalize().multiplyScalar(params.edgeRadius);
      position = subCube.add(addition);
    } else if (
      Math.abs(position.x) > subCubeHalfSize &&
      Math.abs(position.y) > subCubeHalfSize
    ) {
      // Near edge parallel to Z-axis
      addition.z = 0;
      addition.normalize().multiplyScalar(params.edgeRadius);
      position.x = subCube.x + addition.x;
      position.y = subCube.y + addition.y;
    } else if (
      Math.abs(position.x) > subCubeHalfSize &&
      Math.abs(position.z) > subCubeHalfSize
    ) {
      // Near edge parallel to Y-axis
      addition.y = 0;
      addition.normalize().multiplyScalar(params.edgeRadius);
      position.x = subCube.x + addition.x;
      position.z = subCube.z + addition.z;
    } else if (
      Math.abs(position.y) > subCubeHalfSize &&
      Math.abs(position.z) > subCubeHalfSize
    ) {
      // Near edge parallel to X-axis
      addition.x = 0;
      addition.normalize().multiplyScalar(params.edgeRadius);
      position.y = subCube.y + addition.y;
      position.z = subCube.z + addition.z;
    }

    // Apply notches to face surfaces
    const notchOffset = 0.23; // Offset for positioning multiple notches
    const halfSize = params.size / 2;

    if (Math.abs(position.y - halfSize) < 0.01) {
      // Face 1: Y+ (top) - 1 dot
      position.y -= applyNotch([position.x, position.z], params);
    } else if (Math.abs(position.x - halfSize) < 0.01) {
      // Face 2: X+ (right) - 2 dots diagonal
      position.x -= applyNotch(
        [position.y + notchOffset, position.z + notchOffset],
        params
      );
      position.x -= applyNotch(
        [position.y - notchOffset, position.z - notchOffset],
        params
      );
    } else if (Math.abs(position.z - halfSize) < 0.01) {
      // Face 3: Z+ (front) - 3 dots diagonal
      position.z -= applyNotch(
        [position.x - notchOffset, position.y + notchOffset],
        params
      );
      position.z -= applyNotch([position.x, position.y], params);
      position.z -= applyNotch(
        [position.x + notchOffset, position.y - notchOffset],
        params
      );
    } else if (Math.abs(position.z + halfSize) < 0.01) {
      // Face 4: Z- (back) - 4 dots in corners
      position.z += applyNotch(
        [position.x + notchOffset, position.y + notchOffset],
        params
      );
      position.z += applyNotch(
        [position.x + notchOffset, position.y - notchOffset],
        params
      );
      position.z += applyNotch(
        [position.x - notchOffset, position.y + notchOffset],
        params
      );
      position.z += applyNotch(
        [position.x - notchOffset, position.y - notchOffset],
        params
      );
    } else if (Math.abs(position.x + halfSize) < 0.01) {
      // Face 5: X- (left) - 5 dots (4 corners + center)
      position.x += applyNotch(
        [position.y + notchOffset, position.z + notchOffset],
        params
      );
      position.x += applyNotch(
        [position.y + notchOffset, position.z - notchOffset],
        params
      );
      position.x += applyNotch([position.y, position.z], params);
      position.x += applyNotch(
        [position.y - notchOffset, position.z + notchOffset],
        params
      );
      position.x += applyNotch(
        [position.y - notchOffset, position.z - notchOffset],
        params
      );
    } else if (Math.abs(position.y + halfSize) < 0.01) {
      // Face 6: Y- (bottom) - 6 dots (2 rows of 3)
      position.y += applyNotch(
        [position.x + notchOffset, position.z + notchOffset],
        params
      );
      position.y += applyNotch(
        [position.x + notchOffset, position.z],
        params
      );
      position.y += applyNotch(
        [position.x + notchOffset, position.z - notchOffset],
        params
      );
      position.y += applyNotch(
        [position.x - notchOffset, position.z + notchOffset],
        params
      );
      position.y += applyNotch(
        [position.x - notchOffset, position.z],
        params
      );
      position.y += applyNotch(
        [position.x - notchOffset, position.z - notchOffset],
        params
      );
    }

    // Update vertex position
    positionAttr.setXYZ(i, position.x, position.y, position.z);
  }

  // Remove duplicate vertices (caused by BoxGeometry seams)
  boxGeometry.deleteAttribute('normal');
  boxGeometry.deleteAttribute('uv');
  const mergedGeometry = BufferGeometryUtils.mergeVertices(boxGeometry)!;

  // Recompute normals for smooth shading
  mergedGeometry.computeVertexNormals();

  return mergedGeometry;
}

/**
 * Applies smooth notch carving using cosine wave function.
 *
 * **Algorithm (from Codrops):**
 * Creates centered symmetrical smooth impulse using cosine function:
 * 1. Normalize position to -1 to 1 range (using notchRadius)
 * 2. Multiply by π to get wave from -π to π
 * 3. Clamp to -1 to 1 (single wave at center)
 * 4. Apply cosine: (cos(v) + 1) gives 0 to 2 range
 * 5. Multiply by notchDepth for final depth
 *
 * For 3D: Multiply two 1D waves (X and Y) to get 2D impulse
 *
 * @param pos 2D position on face surface
 * @param params Geometry parameters
 * @returns Notch depth at this position
 */
function applyNotch(pos: [number, number], params: DiceGeometryParams): number {
  const notchWave = (v: number): number => {
    v = (1 / params.notchRadius) * v;
    v = Math.PI * Math.max(-1, Math.min(1, v));
    return params.notchDepth * (Math.cos(v) + 1);
  };

  return notchWave(pos[0]) * notchWave(pos[1]);
}

/**
 * Creates inner black planes that show through notches.
 *
 * **Purpose:**
 * Six planes positioned slightly behind each face of the cube.
 * These create black dots visible through the carved notches.
 *
 * @param params Geometry parameters
 * @returns THREE.BufferGeometry (merged planes)
 */
export function createInnerGeometry(
  params: DiceGeometryParams
): THREE.BufferGeometry {
  // Plane size matches flat surface of rounded cube
  const planeSize = params.size - 2 * params.edgeRadius;
  const baseGeometry = new THREE.PlaneGeometry(planeSize, planeSize);

  // Offset planes slightly behind cube faces
  const offset = (params.size / 2) - 0.02;

  // Create 6 planes (one for each face) and merge
  const geometries = [
    baseGeometry.clone().translate(0, 0, offset), // Z+ (front)
    baseGeometry.clone().translate(0, 0, -offset), // Z- (back)
    baseGeometry
      .clone()
      .rotateX(Math.PI / 2)
      .translate(0, -offset, 0), // Y- (bottom)
    baseGeometry
      .clone()
      .rotateX(Math.PI / 2)
      .translate(0, offset, 0), // Y+ (top)
    baseGeometry
      .clone()
      .rotateY(Math.PI / 2)
      .translate(-offset, 0, 0), // X- (left)
    baseGeometry
      .clone()
      .rotateY(Math.PI / 2)
      .translate(offset, 0, 0), // X+ (right)
  ];

  return BufferGeometryUtils.mergeGeometries(geometries, false)!;
}

/**
 * Creates default dice geometry with standard parameters.
 *
 * @returns THREE.Group (complete dice mesh)
 */
export function createDefaultDice(): THREE.Group {
  return createDiceMesh({
    size: 0.6,
    segments: 40,
    edgeRadius: 0.08,
    notchRadius: 0.15,
    notchDepth: 0.17,
  });
}

/**
 * Creates dice material for outer surface.
 *
 * @param color Hex color (default: 0xeeeeee)
 * @returns THREE.MeshStandardMaterial
 */
export function createDiceMaterial(
  color: number = 0xeeeeee
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.7,
    metalness: 0.1,
  });
}

/**
 * Creates material for inner black planes (notch dots).
 *
 * @returns THREE.MeshStandardMaterial
 */
export function createInnerMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 0,
    metalness: 1,
    side: THREE.DoubleSide,
  });
}
